import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import Topbar from "./Topbar";
import {
  getAllowedHostels,
  getAvailableProgrammes,
  getHostelsByProgramme,
  getFloors,
  getRooms,
  getSingleSeaterRequestStatus,
  getBlankFields,
} from "../services/hostelApi";
import { apiGet, fetchFees, refreshFees } from "../services/api";
import { bookRoom } from "../services/hostelApi";
import Modal from "./Modal";
import Toast from "./Toast";
import HostelRulesModal from "./HostelRulesPage";
import { useNavigate } from "react-router-dom";
import Footer from "./Footer";

export default function RoomBooking({ onLogout }) {
  const navigate = useNavigate();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isFirstYear, setIsFirstYear] = useState(false);
  const [programmes, setProgrammes] = useState([]);
  const [selectedProgramme, setSelectedProgramme] = useState("");
  const [hostels, setHostels] = useState([]);
  const [floors, setFloors] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedHostel, setSelectedHostel] = useState("");
  const [selectedFloor, setSelectedFloor] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [feesData, setFeesData] = useState(null);
  const [feesLoading, setFeesLoading] = useState(false);
  const [feesError, setFeesError] = useState("");

  const [seaterRequestStatus, setSeaterRequestStatus] = useState(null); 
  const [confirmData, setConfirmData] = useState(null);
  const [currentAllocation, setCurrentAllocation] = useState(null); 
  const [blankFields, setBlankFields] = useState(null);
  const [blankFieldsError, setBlankFieldsError] = useState("");
  const token = localStorage.getItem("token");

  const [toast, setToast] = useState(null);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [rulesAccepted, setRulesAccepted] = useState(false);

  // Auto-redirect if blank fields detected
  useEffect(() => {
    if (blankFields && blankFields.length > 0) {
      navigate("/edit-details", { replace: true });
    }
  }, [blankFields, navigate]);

  // Auto-clear toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Show rules modal on mount
  useEffect(() => {
    setShowRulesModal(true);
  }, []);

  useEffect(() => {
    async function loadInitialData() {
      setLoading(true);
      setError("");
      try {
        const res = await getAllowedHostels();
        if (res.error) {
          setError(res.error);
        } else {
          setIsFirstYear(res.isFirstYear);
          if (res.currentAllocation) {
            setCurrentAllocation(res.currentAllocation);
          }
          if (res.isFirstYear && res.programmes) {
            setProgrammes(res.programmes);
          } else if (res.success && res.hostels) {
            setHostels(res.hostels);
          }
        }
      } catch (err) {
        setError("Unable to fetch hostel data.");
      } finally {
        setLoading(false);
      }
    }

    async function loadSeaterStatus() {
      try {
        const res = await getSingleSeaterRequestStatus();
        if (res.success) setSeaterRequestStatus(res.request);
      } catch (err) {
        console.error("Unable to fetch seater request status.");
      }
    }

    async function loadBlankFields() {
      setBlankFieldsError("");
      try {
        const res = await getBlankFields();
        if (res.success) {
          setBlankFields(res.blank_fields || []);
        } else if (res.error) {
          setBlankFieldsError(res.error);
        }
      } catch (err) {
        console.error("Unable to fetch blank fields.");
      }
    }

    loadInitialData();
    loadSeaterStatus();
    loadBlankFields();
  }, []);

  useEffect(() => {
    async function loadFees() {
      setFeesLoading(true);
      setFeesError("");
      try {
        const data = await fetchFees(token);
        if (data.success && data.sessions && data.sessions.length > 0) {
          // Explicitly target the 2026-2027 session
          const session2026 = data.sessions.find(
            (s) => s.session_name === "2026-2027"
          );

          if (session2026) {
            setFeesData({
              sessionFound: true,
              allowed: session2026.allowed_to_allot_hostel, // 0 or 1
              seater: session2026.allowed_hostel_seater,
            });
          } else {
            // Session 2026-2027 is missing entirely
            setFeesData({
              sessionFound: false,
              allowed: 0,
              seater: null,
            });
            setFeesError("Critical error: Session 2026-2027 breakdown not found.");
          }
        } else {
          setFeesError("No valid fee data found.");
        }
      } catch (err) {
        setFeesError("Unable to fetch fees data.");
      } finally {
        setFeesLoading(false);
      }
    }

    loadFees();
  }, [token]);

  async function handleProgrammeChange(e) {
    const id = e.target.value;
    setSelectedProgramme(id);
    setSelectedHostel("");
    setSelectedFloor("");
    setFloors([]);
    setRooms([]);
    if (id) {
      const res = await getHostelsByProgramme(id);
      if (res.success) setHostels(res.hostels);
    }
  }

  async function handleHostelChange(e) {
    const id = e.target.value;
    setSelectedHostel(id);
    setSelectedFloor("");
    setFloors([]);
    setRooms([]);
    if (id) {
      const res = await getFloors(id);
      if (res.success) setFloors(res.floors);
    }
  }

  async function handleFloorChange(e) {
    const id = e.target.value;
    setSelectedFloor(id);
    setRooms([]);
    setRoomsLoading(true);

    if (id && feesData?.seater) {
      const res = await getRooms(selectedHostel, id, feesData.seater);
      if (res.success) setRooms(res.rooms);
    }

    setRoomsLoading(false);
  }

  function handleSelectRoom(room) {
    // Structural safeguards check
    if (!feesData || feesData.sessionFound === false || feesData.allowed === 0) {
      setToast({
        type: "error",
        message: "❌ Action denied: You do not have current session clearance.",
      });
      return;
    }

    if (blankFields && blankFields.length > 0) {
      setToast({
        type: "error",
        message: `❌ Complete Your Profile First: ${blankFields.join(", ")}`,
      });
      setTimeout(() => {
        navigate("/edit-details");
      }, 1500);
      return;
    }

    if (currentAllocation && currentAllocation.room_id === room.id) {
      setToast({
        type: "warning",
        message: "⚠️ This is your current room. You cannot rebook the same room.",
      });
      return;
    }
    setConfirmData({ hostel: selectedHostel, room });
  }

  async function confirmBooking() {
    if (!feesData || feesData.sessionFound === false || feesData.allowed === 0) return;
    const res = await bookRoom(confirmData.hostel, confirmData.room.id);

    if (res.success) {
      setToast({
        type: "success",
        message: res.message || "Room booked successfully!",
      });
      setTimeout(() => {
        navigate(res.redirect || "/dashboard");
      }, 1500);
    } else {
      setToast({
        type: "error",
        message: res.error || "Room booking failed. Try again.",
      });
    }
    setConfirmData(null);
  }

  useEffect(() => {
    if (toast && selectedHostel && selectedFloor && feesData?.seater) {
      const refreshRooms = async () => {
        setRoomsLoading(true);
        const result = await getRooms(
          selectedHostel,
          selectedFloor,
          feesData.seater
        );
        if (result.success) setRooms(result.rooms);
        setRoomsLoading(false);
      };
      refreshRooms();
    }
  }, [toast]);

  const isBlockedDueToPendingRequest =
    feesData?.seater === "double" && seaterRequestStatus?.status === "pending";

  if (blankFields && blankFields.length > 0) {
    return null;
  }

  // Master eligibility flag
  const isEligibleFor2026 = feesData && feesData.sessionFound === true && feesData.allowed !== 0;

  return (
    <div className="flex min-h-screen bg-gray-50">
      <HostelRulesModal
        isOpen={showRulesModal}
        onAccept={() => {
          setRulesAccepted(true);
          setShowRulesModal(false);
        }}
        onClose={() => {
          setShowRulesModal(false);
        }}
      />

      <Sidebar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        onLogout={onLogout}
      />

      {sidebarOpen && (
        <div
          className="fixed inset-0 z-20 bg-black bg-opacity-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex-1 flex flex-col md:ml-64">
        <Topbar setSidebarOpen={setSidebarOpen} />

        <main className="flex-1 overflow-y-auto p-6 space-y-6">
          <h2 className="text-2xl font-semibold">Room Booking</h2>

          {/* Error Banner for Missing Session */}
          {feesData?.sessionFound === false && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-800 p-4 rounded mb-4">
              ❌ <strong>Access Blocked:</strong> Your accounts record does not show an active setup for the 2026-2027 academic session. Please contact the administrator.
            </div>
          )}

          {/* Error Banner for 0 Eligibility */}
          {feesData?.sessionFound === true && feesData?.allowed === 0 && (
            <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-800 p-4 rounded mb-4">
              ⚠️ You are not allowed to book a room due to insufficient fee payments for the 2026-2027 session.
            </div>
          )}

          {isBlockedDueToPendingRequest && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-800 p-4 rounded mb-4">
              ❌ You have already requested a Single Seater upgrade. Please wait for approval.
            </div>
          )}

          {feesError && <p className="text-red-600 font-medium">{feesError}</p>}
          {error && <p className="text-red-600">{error}</p>}
          {loading || feesLoading ? <p className="text-gray-500">Loading details...</p> : null}
          {toast && <Toast type={toast.type} message={toast.message} />}

          {/* Only prompt for rules if they are completely cleared for 2026-2027 */}
          {isEligibleFor2026 && !rulesAccepted && !isBlockedDueToPendingRequest && (
            <div className="bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 p-4 rounded mb-4">
              ⚠️ <strong>Rules Required:</strong> You must accept the hostel rules to proceed with room booking.
              <br />
              <button
                onClick={() => setShowRulesModal(true)}
                className="mt-3 bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700 transition"
              >
                📋 Review Hostel Rules
              </button>
            </div>
          )}

          {/* DROPDOWNS & WORKFLOW - STRICTLY SECURED */}
          {isEligibleFor2026 && rulesAccepted && !isBlockedDueToPendingRequest && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                {isFirstYear && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      🎓 Select Programme
                    </label>
                    <select
                      value={selectedProgramme}
                      onChange={handleProgrammeChange}
                      className="block w-full px-4 py-2 text-sm border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
                    >
                      <option value="">-- Choose Programme --</option>
                      {programmes.map((p) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    🏢 Select Hostel
                  </label>
                  <select
                    value={selectedHostel}
                    onChange={handleHostelChange}
                    disabled={isFirstYear && !selectedProgramme}
                    className="block w-full px-4 py-2 text-sm border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition disabled:opacity-50"
                  >
                    <option value="">-- Choose Hostel --</option>
                    {hostels.map((h) => (
                      <option key={h.id} value={h.id}>{h.name}</option>
                    ))}
                  </select>
                </div>

  <div>
  <label className="block text-sm font-medium text-gray-700 mb-1">
    🪜 Select Floor
  </label>
  <select
    value={selectedFloor}
    onChange={handleFloorChange}
    disabled={!selectedHostel}
    className="block w-full px-4 py-2 text-sm border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition disabled:opacity-50"
  >
    <option value="">-- Choose Floor --</option>
    {floors.map((f) => {
      let floorName = "";
      const floorNum = parseInt(f.floor_number, 10);
      
      if (floorNum === 0) floorName = "Ground Floor";
      else if (floorNum === 1) floorName = "First Floor";
      else if (floorNum === 2) floorName = "Second Floor";
      else if (floorNum === 3) floorName = "Third Floor";

      return (
        <option key={f.id} value={f.id}>
          {floorName}
        </option>
      );
    })}
  </select>
</div>
              </div>

              {roomsLoading ? (
                <div className="flex justify-center items-center py-10">
                  <div className="text-center">
                    <div className="w-10 h-10 border-4 border-blue-500 border-dashed rounded-full animate-spin mx-auto mb-3"></div>
                    <p className="text-gray-600 text-sm">Loading available rooms...</p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mt-4">
                  {rooms.map((room) => (
                    <div
                      key={room.id}
                      className={`rounded-xl p-3 shadow-sm border transition-transform duration-200 text-center text-sm select-none cursor-pointer
                        ${room.remaining_slots > 0
                          ? "bg-green-50 text-green-700 border-green-200 hover:scale-105 hover:shadow-md"
                          : "bg-red-50 text-red-600 border-red-200 cursor-not-allowed"
                        }`}
                      onClick={() => room.remaining_slots > 0 && handleSelectRoom(room)}
                    >
                      <div className="font-semibold text-base mb-1">Room {room.room_number}</div>
                      <div className="text-xs text-gray-500">🧍 Capacity: {room.capacity}</div>
                      <div className="mt-1 font-medium">
                        {room.remaining_slots > 0
                          ? `✅ ${room.remaining_slots} Slot${room.remaining_slots > 1 ? "s" : ""} Available`
                          : "🚫 Fully Occupied"}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </main>
        <Footer />
      </div>

      {confirmData && (
        <Modal onClose={() => setConfirmData(null)}>
          <div className="space-y-5 text-center">
            <h2 className="text-xl font-bold text-gray-800">🛏️ Confirm Room Booking</h2>
            
            {currentAllocation && (
              <div className="bg-blue-50 border-l-4 border-blue-400 text-blue-800 p-3 rounded text-sm">
                <strong>📍 Your Current Allocation:</strong>
                <br />
                Hostel: {currentAllocation.hostel_name} | Room: {currentAllocation.room_number}
              </div>
            )}

            <p className="text-gray-700">
              You are booking <strong>Room {confirmData.room.room_number}</strong> with capacity for <strong>{confirmData.room.capacity} students</strong>.
            </p>
            
            <p className="text-xs text-gray-500 italic bg-gray-100 p-2 rounded">
              ⚠️ <strong>Important:</strong> Once confirmed, your room assignment cannot be changed or transferred.
            </p>
            
            <div className="flex justify-center gap-4 mt-4">
              <button
                onClick={confirmBooking}
                className="bg-blue-600 text-white px-5 py-2 rounded hover:bg-blue-700 shadow-md"
              >
                ✅ Confirm Booking
              </button>
              <button
                onClick={() => setConfirmData(null)}
                className="bg-gray-300 text-gray-700 px-5 py-2 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}